import debounce from "lodash/debounce"

export const useTableStore = () => {

    const name = ref(null)
    const route = ref(null)
    const routeParams = ref([])
    const defaultOrder = ref(null)
    const data = ref({}) // store previously fetched data
    const columns = ref(null)
    const filters = ref(null)
    const baseFilters = ref([]) // static module filters, kept separate so dynamic ones can be re-merged idempotently
    const quickFilters = ref(null)
    const tools = ref(null)

    const activeTool = ref(null)
    const loading = ref(false)
    const inverseSelection = ref(false)
    const selectedRows = ref([])
    const filterOptionCache = ref([])

    const listing = ref({
        search: "",
        orderBy: "id",
        orderDirection: "asc",
        perPage: 10,
        perPageOptions: [10, 50, 100],
        columns: 6,
        page: 1,
        lastPage: 1,
        total: 0,
        custom: {},
        filter: [],
        links: []
    })

    const rows = computed(() => {
        return data.value[listing.value.page] || []
    })

    const selectedCount = computed(() => {
        return inverseSelection.value ? listing.value.total - selectedRows.value.length : selectedRows.value.length
    })

    function resetSelection() {
        inverseSelection.value = false
        selectedRows.value = []
    }

    async function preloadPages() {
        const {$apiRoute} = useNuxtApp()
        for (let i = -1; i <= 2; i++) {
            const pageToLoad = listing.value.page + i
            if (pageToLoad < 1 || pageToLoad > listing.value.lastPage || data.value[pageToLoad]) continue
            try {
                const params = {}
                routeParams.value.forEach((param) => {
                    params[param.param] = listing.value.custom[param.prop]
                })
                const response = await $lara.get($apiRoute(route.value + '.index', params), {
                    params: {...listing.value, page: pageToLoad}
                })
                data.value[pageToLoad] = response.data
            } catch (error) {
                console.log(error)
            }
        }
    }

    const preloadPagesDebounced = debounce(preloadPages, 1000, {maxWait: 5000})

    async function fetchData(fresh = false) {
        const {$apiRoute} = useNuxtApp()

        loading.value = true

        preloadPagesDebounced?.cancel()

        if (fresh || data.value[listing.value.page]) {
            data.value = {}
        }

        const params = {}
        routeParams.value.forEach((param) => {
            params[param.param] = listing.value.custom[param.prop]
        })

        $lara.get($apiRoute(route.value + '.index', params), {
            params: {...listing.value}
        }).then((response: Object) => {
            data.value[listing.value.page] = response.data
            listing.value.page = response.meta?.current_page ?? response.current_page
            listing.value.lastPage = response.meta?.last_page ?? response.last_page
            listing.value.total = response.meta?.total ?? response.total
            listing.value.links = response.meta?.links ?? response.links

            if (listing.value.page > listing.value.lastPage) {
                listing.value.page = listing.value.lastPage
            }

            preloadPagesDebounced()
        }).finally(() => {
            loading.value = false
        })
    }

    async function postToolData(tool: string, data: Object = {}) {
        const {$apiRoute} = useNuxtApp()
        const params = {}
        routeParams.value.forEach((param) => {
            params[param.param] = listing.value.custom[param.prop]
        })
        return $lara.post($apiRoute(route.value + '.action', params), {
            rawResponse: data.rawResponse ?? false,
            body: {
                ...listing.value,
                ...listing.value.custom,
                selection: {
                    inverseSelection: inverseSelection.value ?? false,
                    selectedIds: selectedRows.value ?? []
                },
                tool,
                toolData: data
            }
        })
    }

    // Tables scoped to a parent (e.g. participants -> event, via the module's routeParams) cache their filters
    // per parent, so filters from one event don't leak into another. Several routeParams compose into the key,
    // supporting multiple parent levels. Parent-less tables keep the plain `filters-<name>` key (unchanged).
    const parentReady = computed(() => (routeParams.value ?? []).every(param => listing.value.custom?.[param.prop] != null))
    const parentKeyFor = (custom?: Record<string, unknown>) => (routeParams.value ?? [])
        .map(param => `${param.prop}:${custom?.[param.prop] ?? ''}`)
        .join('|')
    const storageKeyFor = (key: string) => key ? `filters-${name.value}-${key}` : `filters-${name.value}`
    const parentKey = computed(() => parentKeyFor(listing.value.custom))
    const filterStorageKey = computed(() => storageKeyFor(parentKey.value))

    // Filters handed in from another page, replacing the active ones. They go to storage for the given parent too:
    // before the parent is ready restoreFilters() would overwrite the listing, and while the store still sits on
    // another parent the save watcher would file them under that parent's key.
    function presetFilters(filters: Object[], parent: Record<string, unknown> = {}) {
        const key = parentKeyFor(parent)
        localStorage.setItem(storageKeyFor(key), JSON.stringify(filters))
        if (parentReady.value && parentKey.value === key) listing.value.filter = filters
    }

    function restoreFilters() {
        if (!name.value) return
        const stored = localStorage.getItem(filterStorageKey.value)
        const restored = stored
            ? JSON.parse(stored)
            : (quickFilters.value ?? []).filter(filter => filter.default).map(filter => ({...filter}))
        // Skip a no-op empty->empty replace so we don't churn the reference and trigger a needless refetch.
        if (!restored.length && !(listing.value.filter?.length)) return
        listing.value.filter = restored
    }

    function initialize(module: string) {
        const moduleDefinition = tableModules[module]
        name.value = moduleDefinition.name
        route.value = moduleDefinition.route
        routeParams.value = moduleDefinition.routeParams ?? []
        defaultOrder.value = moduleDefinition.defaultOrder ?? null
        columns.value = moduleDefinition.columns
        filters.value = moduleDefinition.filters ?? null
        baseFilters.value = moduleDefinition.filters ?? []
        quickFilters.value = moduleDefinition.quickFilters ?? null
        tools.value = moduleDefinition.tools ?? null

        if (defaultOrder.value) {
            listing.value.orderBy = defaultOrder.value.column
            listing.value.orderDirection = defaultOrder.value.direction
        }

        /** Everything a header click can produce, plus the module default — the only legal orderBy values. */
        function sortableColumnNames(): string[] {
            const fromColumns = (columns.value ?? [])
                .filter(column => column.sortable)
                .map(column => column.sortBy ?? column.name)

            return [...fromColumns, defaultOrder.value?.column].filter(Boolean) as string[]
        }

        // Filters are restored per parent by the filterStorageKey watch below — the parent context
        // (routeParams via listing.custom) isn't known yet at initialize time.

        // Load orderBy and orderDirection from local storage
        const storedOrderBy = localStorage.getItem(`${name.value}-orderBy`)
        const storedOrderDirection = localStorage.getItem(`${name.value}-orderDirection`)

        // Only if the module still sorts by it: a stored column that was since renamed or removed would
        // otherwise be sent forever and 500 the endpoint on ORDER BY.
        if (storedOrderBy && sortableColumnNames().includes(storedOrderBy)) {
            listing.value.orderBy = storedOrderBy
        } else if (storedOrderBy) {
            localStorage.removeItem(`${name.value}-orderBy`)
        }
        if (storedOrderDirection) {
            listing.value.orderDirection = storedOrderDirection
        }
    }

    // Append runtime filters (e.g. per-event question filters) to the static module filters.
    function setDynamicFilters(dynamicFilters) {
        filters.value = [...baseFilters.value, ...(dynamicFilters ?? [])]
    }

    // A selection belongs to the parent it was made under. The store is a per-module singleton, so without this the
    // ids outlive the event: the header renders half-checked over rows none of them are in, and an inverse selection
    // — "all except these" — resolves to every participant of the event just opened. Dropped rather than cached per
    // parent, so a stale "select all" cannot come back at all.
    watch(parentKey, () => resetSelection())

    // Restore filters whenever the (parent-aware) storage key becomes ready or changes (e.g. switching events).
    watch(filterStorageKey, () => {
        if (parentReady.value) restoreFilters()
    }, {immediate: true})

    // Watch filters and persist them under the parent-aware key.
    watch(() => listing.value.filter, (newFilters) => {
        if (!name.value || !parentReady.value) return
        localStorage.setItem(filterStorageKey.value, JSON.stringify(newFilters ?? []))
    }, {deep: true})

    // Watch orderBy and update local storage
    watch(() => listing.value.orderBy, (newOrderBy) => {
        localStorage.setItem(`${name.value}-orderBy`, newOrderBy)
    })

    // Watch orderDirection and update local storage
    watch(() => listing.value.orderDirection, (newOrderDirection) => {
        localStorage.setItem(`${name.value}-orderDirection`, newOrderDirection)
    })

    return {
        loading,
        columns,
        filters,
        quickFilters,
        tools,
        rows,
        listing,
        inverseSelection,
        selectedRows,
        selectedCount,
        activeTool,
        defaultOrder,
        route,
        filterOptionCache,
        initialize,
        fetchData,
        resetSelection,
        postToolData,
        presetFilters,
        setDynamicFilters
    }
}